import { createContext, useContext, useEffect, useState } from "react";
import { fetchPluginList } from "../api/Api";
import type { PluginListData, PluginListEntry } from "../model/Model";

export const CACHE_TTL_MS = 5 * 60 * 1000;

export interface PluginListContextValue {
  data: PluginListData | null;
  plugins: PluginListEntry[];
  pluginMap: Map<string, PluginListEntry>;
  loading: boolean;
  error: boolean;
  refresh: () => Promise<void>;
}

export const PluginListContext = createContext<PluginListContextValue | undefined>(
  undefined,
);

export function usePluginList() {
  const ctx = useContext(PluginListContext);
  if (!ctx)
    throw new Error("usePluginList must be used within PluginListProvider");
  return ctx;
}

interface PluginCacheState {
  data: PluginListData | null;
  fetchedAt: number;
  inflight: Promise<PluginListData | null> | null;
  subscribers: Set<(data: PluginListData | null) => void>;
}

export const pluginCache: PluginCacheState = {
  data: null,
  fetchedAt: 0,
  inflight: null,
  subscribers: new Set(),
};

export function loadPluginList(): Promise<PluginListData | null> {
  if (pluginCache.inflight) return pluginCache.inflight;
  pluginCache.inflight = fetchPluginList()
    .then((res) => (res.ok ? (res.json() as Promise<PluginListData>) : null))
    .then((data) => {
      if (data?.plugins) {
        pluginCache.data = data;
        pluginCache.fetchedAt = Date.now();
        for (const fn of pluginCache.subscribers) fn(data);
        return data;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      pluginCache.inflight = null;
    });
  return pluginCache.inflight;
}

export interface ShowcaseGuild {
  id: string;
  name: string;
  icon_url: string | null;
  member_count: number;
  vanity_url_code: string | null;
  description: string;
  is_partnered: boolean;
  is_verified: boolean;
}

interface ShowcaseCacheState {
  data: ShowcaseGuild[] | null;
  fetchedAt: number;
  inflight: Promise<ShowcaseGuild[] | null> | null;
}

const showcaseCache: ShowcaseCacheState = {
  data: null,
  fetchedAt: 0,
  inflight: null,
};

function loadShowcase(): Promise<ShowcaseGuild[] | null> {
  if (showcaseCache.inflight) return showcaseCache.inflight;
  showcaseCache.inflight = fetch("/showcase.json")
    .then((res) =>
      res.ok ? (res.json() as Promise<{ guilds?: ShowcaseGuild[] }>) : null,
    )
    .then((data) => {
      if (data?.guilds) {
        showcaseCache.data = data.guilds;
        showcaseCache.fetchedAt = Date.now();
        return data.guilds;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      showcaseCache.inflight = null;
    });
  return showcaseCache.inflight;
}

export function useShowcaseGuilds() {
  const [guilds, setGuilds] = useState<ShowcaseGuild[]>(
    () => showcaseCache.data ?? [],
  );
  const [loading, setLoading] = useState<boolean>(!showcaseCache.data);

  useEffect(() => {
    const fresh =
      showcaseCache.data &&
      Date.now() - showcaseCache.fetchedAt < CACHE_TTL_MS;
    if (fresh) {
      setGuilds(showcaseCache.data!);
      setLoading(false);
      return;
    }
    loadShowcase()
      .then((result) => {
        if (result) setGuilds(result);
      })
      .finally(() => setLoading(false));
  }, []);

  return { guilds, loading };
}
